import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { courseService } from '../../../services/courseService';
import { useAuth } from '../../../contexts/AuthContext';
import BulkCourseUploadModal from './BulkCourseUploadModal';
import { supabase } from '../../../lib/supabase';

const CourseManager = () => {
  const { user, userProfile } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Dynamic categories and levels from global catalog
  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [formData, setFormData] = useState({
    courseName: '',
    category: '',
    courseType: '',
    specialization: '',
    level: '',
    duration: '',
    fees: '',
    showFee: true,
    eligibility: '',
    admissionProcess: '',
    placementStats: '',
    accreditation: ''
  });
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');

  // Load global catalog on mount
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setCatalogLoading(true);
        const { data, error: catalogError } = await supabase?.from('global_course_catalog')?.select('name, type')?.eq('is_active', true)?.order('display_order', { ascending: true })?.order('name', { ascending: true });

        if (catalogError) throw catalogError;

        const cats = (data || [])?.filter(item => item?.type === 'category')?.map(item => ({ value: item?.name?.toLowerCase()?.replace(/[^a-z0-9]+/g, '_'), label: item?.name }));
        const lvls = (data || [])?.filter(item => item?.type === 'level')?.map(item => ({ value: item?.name?.toLowerCase()?.replace(/[^a-z0-9]+/g, '_'), label: item?.name }));

        // Fallback to hardcoded if catalog is empty
        setCategories(cats?.length > 0 ? [...cats, { value: 'other', label: 'Other (Add Custom Category)' }] : [
          { value: 'engineering', label: 'Engineering' },
          { value: 'business_management', label: 'Business & Management' },
          { value: 'medical', label: 'Medical' },
          { value: 'design', label: 'Design' },
          { value: 'law', label: 'Law' },
          { value: 'hospitality_travel', label: 'Hospitality & Travel' },
          { value: 'animation', label: 'Animation' },
          { value: 'mass_communication', label: 'Mass Communication & Media' },
          { value: 'information_technology', label: 'Information Technology' },
          { value: 'humanities_social_sciences', label: 'Humanities & Social Sciences' },
          { value: 'arts', label: 'Arts' },
          { value: 'science', label: 'Science' },
          { value: 'fine_arts_performing_arts', label: 'Fine Arts & Performing Arts' },
          { value: 'architecture', label: 'Architecture' },
          { value: 'accounting', label: 'Accounting' },
          { value: 'commerce', label: 'Commerce' },
          { value: 'finance', label: 'Finance' },
          { value: 'banking', label: 'Banking' },
          { value: 'fashion_design', label: 'Fashion Design' },
          { value: 'literature', label: 'Literature' },
          { value: 'research', label: 'Research' },
          { value: 'other', label: 'Other (Add Custom Category)' }
        ]);
        setLevels(lvls?.length > 0 ? lvls : [
          { value: 'diploma', label: 'Diploma' },
          { value: 'undergraduate', label: 'Undergraduate' },
          { value: 'postgraduate', label: 'Post Graduate' },
          { value: 'integrated_courses', label: 'Integrated Courses' },
          { value: 'postgraduate_diploma', label: 'Post Graduate Diploma' },
          { value: 'doctoral', label: 'Doctoral' }
        ]);
      } catch (err) {
        console.error('Error fetching global catalog:', err);
        // Keep fallback hardcoded values on error
      } finally {
        setCatalogLoading(false);
      }
    };
    fetchCatalog();
  }, []);

  useEffect(() => {
    const instituteId = userProfile?.institute_profiles?.[0]?.id;
    if (instituteId) {
      fetchCourses();
    } else if (userProfile && !instituteId) {
      // User profile loaded but no institute profile found
      setLoading(false);
      setError('Institute profile not found. Please contact support.');
    }
  }, [userProfile]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const instituteId = userProfile?.institute_profiles?.[0]?.id;
      const data = await courseService?.getCoursesByInstitute(instituteId);
      setCourses(data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError(error?.message || 'Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchCourses();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Handle custom category selection
    if (field === 'category') {
      if (value === 'other') {
        setShowCustomCategory(true);
      } else {
        setShowCustomCategory(false);
        setCustomCategory('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate required fields
    if (!formData?.courseName?.trim()) {
      setSubmitError('Course name is required');
      return;
    }
    if (!formData?.category) {
      setSubmitError('Category is required');
      return;
    }
    if (formData?.category === 'other' && !customCategory?.trim()) {
      setSubmitError('Please enter a custom category name');
      return;
    }
    if (!formData?.courseType?.trim()) {
      setSubmitError('Course type is required');
      return;
    }
    if (!formData?.level) {
      setSubmitError('Level is required');
      return;
    }
    if (!formData?.duration?.trim()) {
      setSubmitError('Duration is required');
      return;
    }
    // Fee is now optional - only validate if showFee is true and fee is provided
    if (formData?.showFee && formData?.fees && isNaN(parseFloat(formData?.fees))) {
      setSubmitError('Please enter a valid fee amount');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      
      // Use custom category if 'other' is selected
      const finalCategory = formData?.category === 'other' ? customCategory?.trim() : formData?.category;
      
      const courseData = {
        ...formData,
        category: finalCategory
      };
      
      if (editingCourse) {
        await courseService?.updateCourse(editingCourse?.id, courseData);
        alert('Course updated successfully!');
      } else {
        await courseService?.createCourse({
          ...courseData,
          instituteId: userProfile?.institute_profiles?.[0]?.id
        });
        alert('Course added successfully!');
      }
      await fetchCourses();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving course:', error);
      setSubmitError(error?.message || 'Failed to save course. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    
    // Check if category is a custom one (not in predefined list)
    const isCustomCategory = course?.category && !categories?.some(cat => cat?.value === course?.category);
    
    setFormData({
      courseName: course?.course_name || '',
      category: isCustomCategory ? 'other' : (course?.category || ''),
      courseType: course?.course_type || '',
      specialization: course?.specialization || '',
      level: course?.level || '',
      duration: course?.duration || '',
      fees: course?.fees || '',
      showFee: course?.show_fee !== false,
      eligibility: course?.eligibility || '',
      admissionProcess: course?.admission_process || '',
      placementStats: course?.placement_stats || '',
      accreditation: course?.accreditation || ''
    });
    
    if (isCustomCategory) {
      setShowCustomCategory(true);
      setCustomCategory(course?.category);
    }
    
    setShowAddModal(true);
  };

  const handleDelete = async (courseId) => {
    if (!confirm('Are you sure you want to delete this course?')) return;
    try {
      await courseService?.deleteCourse(courseId);
      await fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingCourse(null);
    setSubmitError(null);
    setShowCustomCategory(false);
    setCustomCategory('');
    setFormData({
      courseName: '',
      category: '',
      courseType: '',
      specialization: '',
      level: '',
      duration: '',
      fees: '',
      showFee: true,
      eligibility: '',
      admissionProcess: '',
      placementStats: '',
      accreditation: ''
    });
  };

  const handleOpenAddModal = () => {
    console.log('Opening Add Course modal');
    setShowAddModal(true);
    setSubmitError(null);
  };

  const handleOpenBulkUploadModal = () => {
    console.log('Opening Bulk Upload modal');
    setShowBulkUploadModal(true);
  };

  const filteredCourses = courses?.filter(course => {
    if (!searchQuery) return true;
    const query = searchQuery?.toLowerCase();
    return course?.course_name?.toLowerCase()?.includes(query) ||
           course?.specialization?.toLowerCase()?.includes(query) ||
           course?.course_type?.toLowerCase()?.includes(query);
  });

  const formatFees = (fees, showFee) => {
    if (showFee === false) return 'Fee not disclosed';
    if (!fees) return 'Contact Institute';
    const amount = parseFloat(fees);
    if (amount >= 100000) {
      return `₹${(amount / 100000)?.toFixed(2)} Lakh`;
    }
    return `₹${amount?.toLocaleString('en-IN')}`;
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Course Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your institute's course offerings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            iconName="Upload"
            iconPosition="left"
            onClick={handleOpenBulkUploadModal}
            className="shadow-md hover:shadow-lg transition-all"
          >
            Bulk Upload
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={handleOpenAddModal}
            className="shadow-lg hover:shadow-xl transition-all"
          >
            Add Course
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search courses by name, type, or specialization..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          iconName="Search"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-muted-foreground">Loading courses...</p>
          <p className="text-xs text-muted-foreground mt-2">This may take a few moments</p>
        </div>
      ) : error ? (
        <div className="text-center py-12 border-2 border-dashed border-destructive/50 rounded-lg bg-destructive/5">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Courses</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">{error}</p>
          <Button onClick={handleRetry} iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        </div>
      ) : filteredCourses?.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="BookOpen" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {searchQuery ? 'No courses found' : 'No courses added yet'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery ? 'Try adjusting your search query' : 'Start by adding your first course offering'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowAddModal(true)} iconName="Plus" iconPosition="left">
              Add Your First Course
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCourses?.map((course) => (
            <div
              key={course?.id}
              className="p-4 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {course?.course_name}
                  </h3>
                  {course?.specialization && (
                    <p className="text-sm text-primary font-medium mb-2">
                      {course?.specialization}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {course?.course_type && (
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md">
                        {course?.course_type}
                      </span>
                    )}
                    {course?.level && (
                      <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded-md capitalize">
                        {course?.level}
                      </span>
                    )}
                    {course?.category && (
                      <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded-md capitalize">
                        {course?.category}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                      <span>{course?.duration || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="IndianRupee" size={14} color="var(--color-muted-foreground)" />
                      <span>{formatFees(course?.fees, course?.show_fee)}</span>
                    </div>
                    {course?.accreditation && (
                      <div className="flex items-center gap-1">
                        <Icon name="Award" size={14} color="var(--color-muted-foreground)" />
                        <span>{course?.accreditation}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => handleEdit(course)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleDelete(course?.id)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-primary to-primary/90 text-white p-6 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon name="BookOpen" size={28} color="white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-heading font-bold">
                      {editingCourse ? 'Edit Course' : 'Add New Course'}
                    </h2>
                    <p className="text-white/90 text-sm">Fill in the course details below</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={24} color="white" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {submitError && (
                <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-destructive">{submitError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubmitError(null)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., Bachelor of Technology in Computer Science"
                    value={formData?.courseName}
                    onChange={(e) => handleInputChange('courseName', e?.target?.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData?.category}
                    onChange={(value) => handleInputChange('category', value)}
                    options={[{ value: '', label: 'Select Category' }, ...categories]}
                    required
                  />
                  {showCustomCategory && (
                    <div className="mt-2">
                      <Input
                        placeholder="Enter custom category name"
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e?.target?.value)}
                        required
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Course Type <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., B.Tech, MBA, MBBS"
                    value={formData?.courseType}
                    onChange={(e) => handleInputChange('courseType', e?.target?.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Specialization
                  </label>
                  <Input
                    placeholder="e.g., Computer Science & Engineering"
                    value={formData?.specialization}
                    onChange={(e) => handleInputChange('specialization', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Level <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData?.level}
                    onChange={(value) => handleInputChange('level', value)}
                    options={[{ value: '', label: 'Select Level' }, ...levels]}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Duration <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., 4 Years"
                    value={formData?.duration}
                    onChange={(e) => handleInputChange('duration', e?.target?.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Total Fees (in ₹)
                  </label>
                  <Input
                    type="number"
                    placeholder="e.g., 400000"
                    value={formData?.fees}
                    onChange={(e) => handleInputChange('fees', e?.target?.value)}
                    disabled={!formData?.showFee}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData?.showFee}
                      onChange={(e) => handleInputChange('showFee', e?.target?.checked)}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-sm font-medium text-foreground">
                      Display course fee publicly
                    </span>
                  </label>
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    Uncheck this if you prefer not to disclose the fee. Students will see "Contact Institute" instead.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Eligibility Criteria
                  </label>
                  <Input
                    placeholder="e.g., 10+2 with Physics, Chemistry, Mathematics (60% minimum)"
                    value={formData?.eligibility}
                    onChange={(e) => handleInputChange('eligibility', e?.target?.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Admission Process
                  </label>
                  <Input
                    placeholder="e.g., JEE Main, JEE Advanced, State CET"
                    value={formData?.admissionProcess}
                    onChange={(e) => handleInputChange('admissionProcess', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Placement Statistics
                  </label>
                  <Input
                    placeholder="e.g., Average Package: ₹8-12 LPA"
                    value={formData?.placementStats}
                    onChange={(e) => handleInputChange('placementStats', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Accreditation
                  </label>
                  <Input
                    placeholder="e.g., AICTE, NBA, UGC"
                    value={formData?.accreditation}
                    onChange={(e) => handleInputChange('accreditation', e?.target?.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t border-border">
                <Button type="button" variant="outline" onClick={handleCloseModal} fullWidth disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" iconName="Save" iconPosition="left" fullWidth disabled={submitting}>
                  {submitting ? 'Saving...' : (editingCourse ? 'Update Course' : 'Add Course')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkUploadModal && (
        <BulkCourseUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={fetchCourses}
        />
      )}
    </div>
  );
};

export default CourseManager;
